import { createSelector } from "@ngrx/store";
import {
  EmployeesActions,
  GET_CURRENT_USER_SUCCESS,
  GET_EMPLOYEE_SUCCESS,
  GET_EMPLOYEES_SUCCESS,
  SET_EMPLOYEES_PARAMS,
  SET_LOADING,
  REMOVE_SIGNATURE_TEMPLATE_SUCCESS,
  UPDATE_CURRENT_USER_SUCCESS,
  CHANGE_EXCLUDED_FROM_SIGNATURES_SUCCESS,
} from "./employees.actions";
import {
  EmployeesState,
  defaultEmployeeListPager,
  Employee,
  AppState,
} from "./employees.interface";

export const initialState: EmployeesState = {
  employees: {
    total: 0,
    data: [],
  },
  params: {
    sortBy: "fullName",
    pager: defaultEmployeeListPager,
    viewType: "EMPLOYEES",
  },
  currentEmployee: null,
  employee: null,
  isLoading: true,
};

export function employeesReducer(
  state: EmployeesState = initialState,
  action: EmployeesActions,
): EmployeesState {
  switch (action.type) {
    case GET_EMPLOYEES_SUCCESS:
      return {
        ...state,
        employees: action.payload,
      };
    case SET_EMPLOYEES_PARAMS:
      return {
        ...state,
        params: action.payload,
      };
    case SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case GET_CURRENT_USER_SUCCESS:
      return {
        ...state,
        currentEmployee: action.payload,
      };
    case UPDATE_CURRENT_USER_SUCCESS:
      return {
        ...state,
        currentEmployee: action.payload as Employee,
      };
    case GET_EMPLOYEE_SUCCESS:
      return {
        ...state,
        employee: action.payload,
      };
    case REMOVE_SIGNATURE_TEMPLATE_SUCCESS:
      const removePayload = action.payload as {
        employeeId?: string;
        email?: string;
        isAlias: boolean;
        internal?: boolean;
      };
      const removeKey = removePayload.isAlias
        ? removePayload.email
        : removePayload.employeeId;
      return {
        ...state,
        employees: {
          ...state.employees,
          data: state.employees.data.map((emp) => {
            const empKey = emp.isAlias ? emp.email : emp.employeeId;
            if (empKey !== removeKey) {
              return emp;
            }

            const updatedEmp = { ...emp };

            if (removePayload.internal) {
              updatedEmp.internalTemplateId = null;
            } else {
              updatedEmp.templateId = null;
            }

            updatedEmp.canBeExcludedFromSignatures =
              !updatedEmp.templateId && !updatedEmp.internalTemplateId;

            return updatedEmp;
          }),
        },
      };
    case CHANGE_EXCLUDED_FROM_SIGNATURES_SUCCESS:
      const changePayload = action.payload as {
        employeeId: string;
        excludedFromSignatures: boolean;
      };
      return {
        ...state,
        employees: {
          ...state.employees,
          data: state.employees.data.map((emp) => {
            if (emp.employeeId !== changePayload.employeeId) {
              return emp;
            }

            return {
              ...emp,
              excludedFromSignatures: changePayload.excludedFromSignatures,
            };
          }),
        },
      };

    default: {
      return state;
    }
  }
}

export const getEmployeesState = (state: AppState) => state.employees;
export const getCurrentEmployeeState = (state: EmployeesState) =>
  state.currentEmployee;
export const getEmployeeState = (state: EmployeesState) => state.employee;
export const getIsLoadingState = (state: EmployeesState) => state.isLoading;

export const getEmployeeListSelector = createSelector(
  getEmployeesState,
  (state: EmployeesState) => state.employees,
);
export const getCurrentEmployeeSelector = createSelector(
  getEmployeesState,
  getCurrentEmployeeState,
);
export const getEmployeeSelector = createSelector(
  getEmployeesState,
  getEmployeeState,
);
export const isLoadingSelector = createSelector(
  getEmployeesState,
  getIsLoadingState,
);
export const getEmployeesParamsSelector = createSelector(
  getEmployeesState,
  (state: EmployeesState) => state.params,
);
